// Sivana collection page helpers (no dependencies).
// 1. "Filter & Sort": opens Horizon's filter panel, which slides in from the right on every screen size.
// 2. "Load More": Horizon's infinite-scroll trigger stays hidden by CSS; the button fetches the next
//    pages itself (see 6). The button disappears on the last page.
// 5. One card per style: each page already skips repeat colors of a style (sections/main-collection.liquid),
//    and this hides a repeat that arrives with a later page (cards carry data-sivana-family from
//    snippets/sivana-color-dots.liquid). Hidden, not removed, so Load More still knows which page it's on.
//    Not on the new colors collection (Theme settings > Sivana > New colors), which shows every color.
// 6. Full rows; at least 24 styles on arrival and 24 more per "Load More" (see below).

const root = document.documentElement;

function initFilterToggle() {
  const toggle = document.querySelector('[data-sivana-filter-toggle]');
  if (!toggle || toggle.dataset.ready) return;
  toggle.dataset.ready = 'true';

  // Every screen size: Horizon's filter panel slides in from the right.
  toggle.addEventListener('click', () => {
    const drawer = document.getElementById('filters-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
}

// 3. Filter bar (sections/sivana-collection-toolbar.liquid): each filter button opens the right-side
//    panel at that filter; Sort by is a dropdown; the bar reloads itself after filtering in the panel.
function openPanelAt(label) {
  const drawer = document.getElementById('filters-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;
  drawer.open();
  const groups = [...drawer.querySelectorAll('details')].filter((details) => details.querySelector('.facets__label'));
  groups.forEach((details) => {
    const name = details.querySelector('.facets__label')?.textContent.trim().toLowerCase();
    details.open = Boolean(label) && name === label.toLowerCase();
  });
  const target = groups.find((details) => details.open);
  if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
}

function initFilterBar() {
  const bar = document.querySelector('[data-sivana-fbar]');
  if (!bar || bar.dataset.ready) return;
  bar.dataset.ready = 'true';

  bar.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-fbar-open]');
    if (pill) return openPanelAt(pill.dataset.fbarOpen);

    const toggle = event.target.closest('[data-fbar-sort-toggle]');
    const menu = bar.querySelector('[data-fbar-sort-menu]');
    if (toggle) {
      const open = menu.hidden;
      menu.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) menu.querySelector('[aria-selected="true"] button, button')?.focus();
      return;
    }
    const option = event.target.closest('[data-sort-value]');
    if (option) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', option.dataset.sortValue);
      url.searchParams.delete('page');
      location.href = url.toString();
    }
  });
  document.addEventListener('click', (event) => {
    const menu = bar.querySelector('[data-fbar-sort-menu]');
    if (!menu || menu.hidden || event.target.closest('[data-fbar-sort]')) return;
    menu.hidden = true;
    bar.querySelector('[data-fbar-sort-toggle]')?.setAttribute('aria-expanded', 'false');
  });
  bar.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = bar.querySelector('[data-fbar-sort-menu]');
    if (menu && !menu.hidden) {
      menu.hidden = true;
      bar.querySelector('[data-fbar-sort-toggle]')?.focus();
    }
  });
}

// Sticky filter bar (assets/sivana-filters.css): a hairline under it once it's stuck under the header
// (at the top of the page the breadcrumb keeps it lower than that), and once the product grid ends the
// grid pushes it up and away so it doesn't hang over the giving back / perks sections below.
function updateStuck() {
  const bar = document.querySelector('[data-sivana-fbar]');
  if (!bar) return;
  const top = parseFloat(getComputedStyle(bar).top) || 0;
  const gridBottom = document.querySelector('results-list')?.getBoundingClientRect().bottom ?? Infinity;
  const push = Math.min(0, gridBottom - (top + bar.offsetHeight));
  bar.style.translate = push ? `0 ${Math.round(push)}px` : '';
  bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top - push <= top + 1);
}
// Capture: on wide screens Horizon scrolls .page-wrapper, not the window, and element scrolls don't bubble.
document.addEventListener('scroll', updateStuck, { capture: true, passive: true });

// After filtering in the panel Horizon redraws only the product grid; reload the bar to match.
let barTimer;
function refreshFilterBar() {
  clearTimeout(barTimer);
  barTimer = setTimeout(async () => {
    const bar = document.querySelector('[data-sivana-fbar]');
    if (!bar) return;
    const url = new URL(location.href);
    url.searchParams.set('section_id', bar.dataset.sectionId);
    try {
      const html = await (await fetch(url)).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sivana-fbar]');
      if (fresh) {
        bar.replaceWith(fresh);
        initFilterBar();
        updateStuck();
      }
    } catch (error) {
      // Network hiccup: the bar catches up on the next change or page load.
    }
  }, 250);
}

function watchResults() {
  const results = document.querySelector('results-list');
  if (!results || results.dataset.sivanaWatch) return;
  results.dataset.sivanaWatch = 'true';
  let lastSearch = location.search;
  new MutationObserver(() => {
    if (location.search === lastSearch) return;
    lastSearch = location.search;
    refreshFilterBar();
  }).observe(results, { childList: true, subtree: true });
}

function grid() {
  return document.querySelector('results-list [ref="grid"]');
}

function lastLoadedPage(list) {
  const cards = list.querySelectorAll(':scope > [data-page]');
  let max = 0;
  cards.forEach((card) => {
    max = Math.max(max, Number(card.dataset.page) || 0);
  });
  return max;
}

function hideRepeatStyles() {
  const list = grid();
  if (!list) return;
  // The new colors collection shows every color as its own card (Theme settings > Sivana > New colors)
  if (document.querySelector('[data-sivana-every-color]')) return;
  const seen = new Set();
  list.querySelectorAll(':scope > li').forEach((card) => {
    const family = card.querySelector('[data-sivana-family]')?.dataset.sivanaFamily;
    if (!family) return;
    const repeat = seen.has(family);
    card.classList.toggle('sivana-repeat-style', repeat);
    if (!repeat) seen.add(family);
  });
}

function updateLoadMore() {
  const wrap = document.querySelector('[data-sivana-load-more-wrap]');
  const list = grid();
  if (!wrap) return;
  if (!list) {
    wrap.hidden = true;
    return;
  }
  wrap.hidden = allLoaded(list) && !list.querySelector(':scope > li.sivana-held');
}

// 6. Full rows and full batches. With one card per style, a page of products can hold only a few new
//    styles, so the page fills itself on arrival until at least 24 styles show, and each "Load More"
//    adds at least 24 more (the theme fetches the next pages itself, quietly, then shows the new cards
//    together). Rows are only shown when full: the odd card waits for the next batch, unless it's the
//    very last one. Waiting cards carry .sivana-held (assets/sivana-color-dots.css). Horizon works out
//    its next page from the last card's data-page, so it stays in step with the cards added here.
const FIRST_SCREEN = 24;
const BATCH = 24;
let loading = false;

function allLoaded(list) {
  return lastLoadedPage(list) >= (Number(list.dataset.lastPage) || 1);
}

function styleCards(list) {
  return [...list.querySelectorAll(':scope > li')].filter((card) => !card.classList.contains('sivana-repeat-style'));
}

function columns(list) {
  return getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
}

function shownCount(list) {
  return styleCards(list).filter((card) => !card.classList.contains('sivana-held')).length;
}

function layoutCards(limit) {
  const list = grid();
  if (!list) return;
  hideRepeatStyles();
  const cards = styleCards(list);
  let show = cards.length;
  if (limit !== undefined) {
    show = Math.min(show, limit);
  } else if (!allLoaded(list)) {
    const cols = columns(list);
    show = Math.max(Math.min(cols, show), show - (show % cols));
  }
  cards.forEach((card, index) => card.classList.toggle('sivana-held', index >= show));
}

async function fetchNextPage(list) {
  const page = lastLoadedPage(list) + 1;
  const sectionId = list.closest('results-list')?.getAttribute('section-id');
  if (!sectionId) return false;
  const url = new URL(window.location.href);
  url.searchParams.set('page', String(page));
  url.searchParams.set('section_id', sectionId);
  url.hash = '';
  const response = await fetch(url.toString());
  if (!response.ok) return false;
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const cards = html.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
  if (!cards.length || grid() !== list) return false;
  list.append(...cards);
  return true;
}

// Fetch pages until there are at least `target` styles (or the collection runs out). While a Load More
// batch is gathering, the new cards wait out of sight (`holdFrom`), then show together.
async function loadUntil(target, holdFrom) {
  const list = grid();
  if (!list || loading) return;
  loading = true;
  try {
    while (!allLoaded(list) && styleCards(list).length < target && grid() === list) {
      if (!(await fetchNextPage(list))) break;
      layoutCards(holdFrom);
    }
  } finally {
    loading = false;
    if (grid() === list) layoutCards();
    updateLoadMore();
  }
}

let cardCount = -1;
let watchedList = null;
function onCardsChanged() {
  const list = grid();
  if (!list) return;
  const count = list.children.length;
  if (list === watchedList && count === cardCount) return;
  const fresh = list !== watchedList;
  watchedList = list;
  cardCount = count;
  if (loading && !fresh) return;
  layoutCards();
  updateLoadMore();
  // A new grid (first visit, or after filtering or sorting): fill the first screen
  if (fresh) loadUntil(FIRST_SCREEN);
}

function initLoadMore() {
  const button = document.querySelector('[data-sivana-load-more]');
  if (!button || button.dataset.ready) return;
  button.dataset.ready = 'true';

  button.addEventListener('click', async () => {
    const list = grid();
    if (!list || loading) return;
    const start = shownCount(list);
    button.setAttribute('aria-busy', 'true');
    await loadUntil(start + BATCH, start);
    button.removeAttribute('aria-busy');
  });
}

// 4. Swipe hint: once per shopping session, when the shopper pauses scrolling, the first product on
//    screen with more than one photo slides a little to peek at the next photo, then springs back.
//    Skipped with reduced motion. Animation: .sivana-swipe-hint in sections/sivana-brand-styles.liquid.
const HINT_KEY = 'sivana-swipe-hint-shown';

function hintShown() {
  try {
    return sessionStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function markHintShown() {
  try {
    sessionStorage.setItem(HINT_KEY, '1');
  } catch {
    // Private mode: the hint may show again on the next page, which is fine.
  }
}

// First card (in page order) that is mostly on screen, still on its first photo, and has another photo.
function hintCard() {
  return [...document.querySelectorAll('results-list .card-gallery slideshow-component')].find((slideshow) => {
    const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
    if (slides.length < 2 || slides[0].getAttribute('aria-hidden') !== 'false') return false;
    const box = slideshow.getBoundingClientRect();
    const visible = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
    return box.height > 0 && visible / box.height >= 0.8;
  });
}

async function playHint(card) {
  // Make sure the next photo is loaded so the peek shows a picture, not a blank.
  const next = card.querySelectorAll('slideshow-slide:not([hidden])')[1]?.querySelector('img');
  if (next) {
    next.loading = 'eager';
    await Promise.race([next.decode().catch(() => {}), new Promise((done) => setTimeout(done, 1500))]);
  }
  if (hintCard() !== card) return false; // scrolled away or swiped while the photo loaded
  markHintShown();
  card.classList.add('sivana-swipe-hint');
  const done = (event) => {
    if (event.animationName !== 'sivana-swipe-hint') return;
    card.removeEventListener('animationend', done);
    card.classList.remove('sivana-swipe-hint');
  };
  card.addEventListener('animationend', done);
  card.addEventListener('pointerdown', () => card.classList.remove('sivana-swipe-hint'), { once: true });
  return true;
}

function initSwipeHint() {
  if (root.dataset.sivanaSwipeHint || hintShown() || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  root.dataset.sivanaSwipeHint = 'true';

  let timer;
  let playing = false;
  const stopped = async () => {
    const card = hintCard();
    if (!card || playing) return;
    playing = true;
    if (await playHint(card)) document.removeEventListener('scroll', onScroll, true);
    playing = false;
  };
  // Listens in the capture phase so it also hears Horizon's desktop page scroller (.page-wrapper).
  const onScroll = (event) => {
    if (event.target instanceof Element && event.target.closest('slideshow-slides')) return; // photo swipes
    clearTimeout(timer);
    timer = setTimeout(stopped, 400);
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
}

// 5. Photo warm-up: Horizon only starts downloading a card's extra photos once the shopper starts
//    swiping, so each swipe waited on the network. Instead, photo 2 loads as the card nears the
//    screen, and the rest load the moment a finger (or mouse) touches the card.
const warmed = new WeakSet();

function cardPhotos(slideshow) {
  return [...slideshow.querySelectorAll('slideshow-slide:not([hidden]) img')];
}

function warm(img) {
  if (!img || img.dataset.sivanaWarm) return;
  img.dataset.sivanaWarm = 'true';
  img.loading = 'eager';
  img.decode().catch(() => {}); // decode ahead too, so the photo paints the instant it slides in
}

function warmAll(slideshow) {
  cardPhotos(slideshow).forEach(warm);
}

const nearScreen = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      nearScreen.unobserve(entry.target);
      warm(cardPhotos(entry.target)[1]);
    });
  },
  { rootMargin: '600px 0px' }
);

function initPhotoWarmup() {
  document.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
    if (warmed.has(slideshow)) return;
    warmed.add(slideshow);
    if (cardPhotos(slideshow).length < 2) return;
    nearScreen.observe(slideshow);
    const touched = () => warmAll(slideshow);
    slideshow.addEventListener('pointerenter', touched, { once: true, passive: true });
    slideshow.addEventListener('pointerdown', touched, { once: true, passive: true });
    slideshow.addEventListener('touchstart', touched, { once: true, passive: true });
  });
}

function init() {
  initFilterBar();
  watchResults();
  initFilterToggle();
  initLoadMore();
  onCardsChanged();
  initSwipeHint();
  initPhotoWarmup();
}

init();

// Horizon re-renders the grid when filters or sorting change, and Load More adds cards.
const results = document.querySelector('results-list');
const watched = results && (results.closest('.shopify-section') || results.parentElement);
if (watched) {
  new MutationObserver(() => {
    onCardsChanged();
    initPhotoWarmup();
  }).observe(watched, { childList: true, subtree: true });
}
